import React, { ChangeEvent, FC, FocusEvent, useRef, useState } from 'react';

import { TypographyVariants } from 'constants/typography-variants';

import { Typography } from 'components/typography';

import closeIconSvg from 'assets/images/svg/close.svg';

import { useStyles } from './styles';

const isValueSet = (value: string | number | undefined, type = 'text'): boolean => {
    if (type === 'number') {
        return Number.isInteger(parseInt(value as string));
    }
    return !!value;
};

export interface InputProps {
    value?: string | number;
    type?: string;
    label?: string;
    disabled?: boolean;
    autoFocus?: boolean;

    onFocus?: (e: FocusEvent) => void;
    onBlur?: (e: FocusEvent) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement> | Event) => void;

    inputWrapperClassName?: string;
    inputLabelClassName?: string;


    inputClassName?: string;

    error?: boolean;

    startAdornment?: string;
    endAdornment?: string;
    closeIcon?: boolean;

    name?: string;

    placeholder?: string;
}

export const Input: FC<InputProps> = ({
        value,
        type,
        label,
        error,
        onFocus,
        onChange,
        startAdornment,
        endAdornment,
        onBlur,
        inputWrapperClassName,
        inputLabelClassName,
        inputClassName,
        closeIcon,
        placeholder,
        ...props
    }) => {

    const {
        inputWrapper,
        input,
        inputLabelClass,
        inputCloseIconClass,
        inputActiveClass,
        inputDisabledClass,
        inputErrorClass,
        inputLabelActiveClass,
        inputLabelErrorClass,
        inputEndAdornmentClass,
        inputPlaceholderActiveClass
    } = useStyles();

    const inputEl = useRef({} as HTMLInputElement);

    const [activeClass, setActiveClass] = useState(false);

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
        if (disabled) return;

        setActiveClass(true);

        if (onFocus) {
            onFocus(e);
        }
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(e);
        }
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        setActiveClass(false);

        if(onBlur) {
            onBlur(e);
        }
    }

    const handleClean = () => {
        const evt = new Event('change');
        const { current } = inputEl;

        current.value = '';

        const event = {
            ...evt,
            target: current,
            currentTarget: current
        }

        if (onChange) {
            onChange(event);
        }

        current.focus();
    }

    const disabled = false;

    const inputType =  type ? type : 'text';

    const combinedInputClass = `${input} ${disabled ? inputDisabledClass: ''} ${inputClassName}`;

    const combinedInputWrapperClass = `${inputWrapper} ${inputWrapperClassName} ${disabled ? inputDisabledClass : ''} ${activeClass ? inputActiveClass : '' } ${error ? inputErrorClass : '' }`;

    const combinedInputLabelClass = `${inputLabelClass} ${inputLabelClassName} ${isValueSet(value, inputType) || activeClass ? inputLabelActiveClass : ''} ${error ? inputLabelErrorClass : '' }`;

    const isEndAdornmentShown = activeClass || error || value;

    const combinedInputPlaceholderClass = `${inputLabelClass} ${inputLabelClassName} ${isValueSet(value, inputType) || activeClass ? inputPlaceholderActiveClass : ''}`;


    return (
        <div className={combinedInputWrapperClass}>
            {startAdornment && (
                <Typography variant={TypographyVariants.inputStartAdornment}>
                    {startAdornment}
                </Typography>
            )}

            {label && (
                <Typography variant={TypographyVariants.inputLabel} className={combinedInputLabelClass}>
                    {label} {endAdornment && !isEndAdornmentShown ? `- ${endAdornment}` : ''}
                </Typography>
            )}

            {placeholder && (
                <Typography variant={TypographyVariants.inputLabel} className={combinedInputPlaceholderClass}>
                    {placeholder}
                </Typography>
            )}
            <input
                ref={inputEl}
                type={inputType}
                disabled={disabled}
                onBlur={handleBlur}
                onFocus={handleFocus}
                className={combinedInputClass}
                value={value}
                onChange={handleChange}
                name={props.name}
                min={0}
                {...props}
            />
            {endAdornment && isEndAdornmentShown && (
                <Typography variant={TypographyVariants.inputEndAdornment} className={inputEndAdornmentClass}>
                    {endAdornment}
                </Typography>
            )}
            {closeIcon && value && (
                <img className={inputCloseIconClass} onClick={handleClean} src={closeIconSvg} alt='Reset' />
            )}
        </div>
    );
}
